import { ColumnControlsContext } from 'components/Table/contexts';
import ColumnsContext from 'components/Table/contexts/ColumnsContext';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { Controller, ControlProps } from '../types';

export const useColumnControl = (columnNames?: string[], state?: ControlProps['state']) => {
  // if columnNames is undefined, we assume we can control all columns
  const hasControl = useCallback(
    (name: string) => {
      return !!columnNames ? columnNames.includes(name) : true;
    },
    [columnNames]
  );
  const { getColumn, columns } = useContext(ColumnsContext);
  const { updateColumn, controlledColumns } = useContext(ColumnControlsContext);
  const allColumnNames = columns.map((col) => col.name).join('');
  const activeColumns = useMemo(
    () => columns.filter((column) => hasControl(column.name)),
    // memo on the string of all the column names, instead of the value itself
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allColumnNames, hasControl]
  );

  const previousColumnNames = useRef<string[]>([]);

  useEffect(() => {
    const lastColumnNames = previousColumnNames.current;
    const newNames =
      activeColumns
        .map((column) => column.name)
        .filter((columnName) => !lastColumnNames?.includes(columnName)) || [];
    lastColumnNames.push(...newNames);
    // the array of column names we control has changed, we only want to update new columns with their state

    if (newNames.length) {
      newNames.forEach((columnName) => {
        const newState = state?.find((state) => state.name === columnName);
        if (newState) {
          updateColumn(newState);
        }
      });
      return;
    }

    if (state) {
      updateColumn(...state);
    }

    // only update if the state or columnNames have changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, columnNames]);

  return {
    controlledColumns,
    hasControl,
    updateColumn,
    getColumn,
  };
};

export const useControlRegistration = (control: Controller) => {
  const { registerControl, unregisterControl } = useContext(ColumnControlsContext);

  useEffect(() => {
    registerControl(control);
  }, [control, registerControl]);

  useEffect(() => {
    return () => {
      unregisterControl(control);
    };
    // unregister on unmount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
